import { ANALYTICS_PLATFORM, ANALYTICS_EVENT, AnalyticsEventParams } from '~/models/Analytics'

type MetricsCallback = (eventType: ANALYTICS_EVENT, params: AnalyticsEventParams) => void

type GtagCommand = 'config' | 'event' | 'set'
type GtagCallbackEvent = (eventType: ANALYTICS_EVENT, params: AnalyticsEventParams) => void
type GtagCallback = (
	command: GtagCommand,
	eventType: ANALYTICS_EVENT,
	params: AnalyticsEventParams
) => void

abstract class BaseMetricsService {
	abstract getClientName(): string
	abstract getContext(routeName: string): string | undefined
}

class MetricsEvent {
	eventType: ANALYTICS_EVENT
	eventParams: AnalyticsEventParams = { platform: ANALYTICS_PLATFORM }
	service: BaseMetricsService

	constructor(type: ANALYTICS_EVENT, routeName?: string) {
		this.eventType = type
		this.service = this.getMetricsService()
		this.setClient()
		this.setContext(routeName)
	}

	title(title: string) {
		this.eventParams.title = title
		return this
	}

	action(action: string) {
		this.eventParams.action = action
		return this
	}

	id(id: string) {
		this.eventParams.id = id
		return this
	}

	destination(destination: string) {
		this.eventParams.destination = destination
		return this
	}

	scope(scope: string) {
		this.eventParams.scope = scope
		return this
	}

	type(type: string) {
		this.eventParams.type = type
		return this
	}

	referer(referer: string) {
		this.eventParams.referer = referer
		return this
	}

	num(num: number) {
		this.eventParams.num = num.toString()
		return this
	}

	addParam(key: string, value: string, addContext = true) {
		const prefix = this.eventParams.context && addContext ? `${this.eventParams.context}_` : ''
		this.eventParams[`${prefix}${key}`] = value
		return this
	}

	send(gtag: GtagCallback | GtagCallbackEvent, mixpanel?: MetricsCallback | any) {
		const needsCommand = gtag.length === 0
		if (needsCommand) {
			;(gtag as GtagCallback)('event', this.eventType, this.eventParams)
		} else {
			;(gtag as GtagCallbackEvent)(this.eventType, this.eventParams)
		}

		if (!mixpanel) return
		const isLibrary = mixpanel.track

		if (isLibrary) {
			mixpanel.track(this.eventType, this.eventParams)
		} else {
			mixpanel(this.eventType, this.eventParams)
		}
	}

	with() {
		return this
	}

	and() {
		return this
	}

	private setClient() {
		const client = this.service.getClientName()
		if (client) this.eventParams.client = client
	}

	private setContext(routeName?: string) {
		if (!routeName) return
		const context = this.service.getContext(routeName)
		if (context) this.eventParams.context = context
	}

	private getMetricsService(): BaseMetricsService {
		try {
			// eslint-disable-next-line @typescript-eslint/no-require-imports
			const file = require('~/services/MetricsService')
			return file.MetricsService.getInstance()
		} catch {
			throw new Error('Metrics Service not implemented')
		}
	}
}

class AddEvent extends MetricsEvent {
	constructor(element: string, routeName?: string) {
		super(ANALYTICS_EVENT.ADD, routeName)
		this.eventParams.element = element
	}
}

class ViewEvent extends MetricsEvent {
	constructor(element: string, routeName?: string) {
		super(ANALYTICS_EVENT.VIEW, routeName)
		this.eventParams.element = element
	}
}

class PageViewEvent extends MetricsEvent {
	constructor(element?: string, routeName?: string) {
		super(ANALYTICS_EVENT.PAGE_VIEW, routeName)
		if (element) this.eventParams.element = element
	}
}

class ClickEvent extends MetricsEvent {
	constructor(element: string, routeName?: string) {
		super(ANALYTICS_EVENT.CLICK, routeName)
		this.eventParams.element = element
	}
}

class CreateEvent extends MetricsEvent {
	constructor(element: string, routeName?: string) {
		super(ANALYTICS_EVENT.CREATE, routeName)
		this.eventParams.element = element
	}
}

class DownloadEvent extends MetricsEvent {
	constructor(element: string, routeName?: string) {
		super(ANALYTICS_EVENT.DOWNLOAD, routeName)
		this.eventParams.element = element
	}
}

class ShareEvent extends MetricsEvent {
	constructor(element: string, routeName?: string) {
		super(ANALYTICS_EVENT.SHARE, routeName)
		this.eventParams.element = element
	}
}

class EditEvent extends MetricsEvent {
	constructor(element: string, routeName?: string) {
		super(ANALYTICS_EVENT.EDIT, routeName)
		this.eventParams.element = element
	}
}

class DeleteEvent extends MetricsEvent {
	constructor(element: string, routeName?: string) {
		super(ANALYTICS_EVENT.DELETE, routeName)
		this.eventParams.element = element
	}
}

class SearchEvent extends MetricsEvent {
	constructor(query: string, routeName?: string) {
		super(ANALYTICS_EVENT.SEARCH, routeName)
		this.eventParams.query = query
	}
}

class RegisterEvent extends MetricsEvent {
	constructor(channel?: string, routeName?: string) {
		super(ANALYTICS_EVENT.REGISTER, routeName)
		if (channel) this.eventParams.channel = channel
	}
}

class RegistrationCompleteEvent extends MetricsEvent {
	constructor() {
		super(ANALYTICS_EVENT.REGISTRATION_COMPLETE)
	}
}

export {
	BaseMetricsService,
	AddEvent,
	RegisterEvent,
	SearchEvent,
	ViewEvent,
	PageViewEvent,
	ClickEvent,
	CreateEvent,
	DownloadEvent,
	ShareEvent,
	EditEvent,
	DeleteEvent,
	RegistrationCompleteEvent,
}
